import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import {
  AideMedicalExpenseData,
  MEDICAL_FEE_FREQUENCIES,
  MedicalFeeFrequency,
  emptyLiveInAideState,
} from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 5 — Medical expense (RESPROP LIA Questionnaire p2, "Medical Expense
// Verification"). One Yes/No question: does the resident pay the aide a fee for
// live-in-aide services? When Yes, reveal a fee amount ($) + frequency
// (Yearly/Monthly/Weekly). Amount + frequency are required only when the fee
// answer is Yes; when No, both are hidden AND cleared (so a partially-filled
// "Yes" state can't leak stale values into the payload). Reuses the affordable
// Y/N ToggleButton pattern (residentPaysFee stored as boolean|null, `null` ==
// unanswered) and the revealed-TextField pattern from AideIdentity.
// ---------------------------------------------------------------------------

interface Props {
  /** Persisted medical-expense slice (state.medicalExpense). */
  defaultValues?: Partial<AideMedicalExpenseData>;
  onSubmit: (data: AideMedicalExpenseData) => void | Promise<void>;
  isSaving?: boolean;
}

export const AideMedicalExpense: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const requiredMsg = t(
    "affordable.application.liveInAide.medicalExpense.required"
  ) as string;

  const schema = yup.object({
    residentPaysFee: yup.boolean().nullable().required(requiredMsg),
    // Amount + frequency are required only when the resident pays a fee.
    amount: yup.string().when("residentPaysFee", {
      is: true,
      then: (s) =>
        s.required(
          t(
            "affordable.application.liveInAide.medicalExpense.amountRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    frequency: yup.string().when("residentPaysFee", {
      is: true,
      then: (s) =>
        s.required(
          t(
            "affordable.application.liveInAide.medicalExpense.frequencyRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AideMedicalExpenseData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      residentPaysFee:
        defaultValues?.residentPaysFee ??
        emptyLiveInAideState.medicalExpense.residentPaysFee,
      amount: defaultValues?.amount ?? emptyLiveInAideState.medicalExpense.amount,
      frequency:
        defaultValues?.frequency ??
        emptyLiveInAideState.medicalExpense.frequency,
    },
  });

  const residentPaysFee = watch("residentPaysFee");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.medicalExpense.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.medicalExpense.subtitle")}
        </Typography>

        {/* Y/N — does the resident pay the aide a fee? */}
        <Controller
          name="residentPaysFee"
          control={control}
          render={({ field }) => (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                  {t(
                    "affordable.application.liveInAide.medicalExpense.feeQuestion"
                  )}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={
                    field.value === true
                      ? "yes"
                      : field.value === false
                      ? "no"
                      : null
                  }
                  onChange={(_, val) => {
                    if (val === null) return;
                    const yes = val === "yes";
                    field.onChange(yes);
                    // When switching to No, clear the revealed fields so stale
                    // values can't leak into the persisted payload.
                    if (!yes) {
                      setValue("amount", "");
                      setValue("frequency", "");
                    }
                  }}
                  sx={{ flexShrink: 0 }}
                >
                  <ToggleButton value="yes" sx={{ px: 2 }}>
                    {t("affordable.application.liveInAide.medicalExpense.yes")}
                  </ToggleButton>
                  <ToggleButton value="no" sx={{ px: 2 }}>
                    {t("affordable.application.liveInAide.medicalExpense.no")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {errors.residentPaysFee && (
                <FormHelperText error>
                  {errors.residentPaysFee.message as string}
                </FormHelperText>
              )}
            </Box>
          )}
        />

        {/* Revealed only when the resident pays a fee. */}
        {residentPaysFee === true && (
          <Box sx={{ mt: 1 }}>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.liveInAide.medicalExpense.amountLabel"
                  )} *`}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                  onChange={(e) =>
                    field.onChange(e.target.value.replace(/[^\d.]/g, ""))
                  }
                  sx={{ mb: 3 }}
                />
              )}
            />

            <Controller
              name="frequency"
              control={control}
              render={({ field }) => (
                <Box>
                  <Typography
                    color="primary"
                    sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}
                  >
                    {t(
                      "affordable.application.liveInAide.medicalExpense.frequencyLabel"
                    )}{" "}
                    *
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={field.value || null}
                    onChange={(_, val: MedicalFeeFrequency | null) => {
                      if (val !== null) field.onChange(val);
                    }}
                  >
                    {MEDICAL_FEE_FREQUENCIES.map((freq) => (
                      <ToggleButton key={freq} value={freq} sx={{ px: 2 }}>
                        {t(
                          `affordable.application.liveInAide.medicalExpense.frequencies.${freq}`
                        )}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {errors.frequency && (
                    <FormHelperText error>
                      {errors.frequency.message as string}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />
          </Box>
        )}
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
